import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import { useErrorAlert } from './AlertSystem';
import { alertsApi, reportsApi, blogsApi, userApi } from '../lib/api';
import './DashboardOverview.css';

interface Alert {
    id: number;
    title: string;
    description: string;
    type: 'info' | 'warning' | 'error' | 'success' | 'critical';
    category: 'security' | 'threat' | 'performance' | 'network' | 'system' | 'application' | 'compliance';
    priority: 'low' | 'medium' | 'high' | 'critical';
    status: 'active' | 'acknowledged' | 'investigating' | 'resolved' | 'dismissed' | 'escalated';
    created_at: string;
}

interface Report {
    id: number;
    title: string;
    description: string;
    platform?: string;
    threatType?: string;
    affectedCompany?: string;
    threatScore?: number | string | null;
    createdAt: string;
    views: number;
}

interface Blog {
    id: number;
    title: string;
    excerpt?: string;
    category?: string;
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    views: number;
    createdAt: string;
    author?: {
        id: number;
        name: string;
    };
}

interface User {
    id: number;
    name: string;
    email: string;
    role: string;
}

interface DashboardData {
    user: User | null;
    alerts: Alert[];
    reports: Report[];
    blogs: Blog[];
    stats: {
        totalAlerts: number;
        activeAlerts: number;
        totalReports: number;
        totalBlogs: number;
    };
}

const DashboardOverview: React.FC = () => {
    const [dashboardData, setDashboardData] = useState<DashboardData>({
        user: null,
        alerts: [],
        reports: [],
        blogs: [],
        stats: {
            totalAlerts: 0,
            activeAlerts: 0,
            totalReports: 0,
            totalBlogs: 0
        }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const showError = useErrorAlert();

    // Optimized data fetching with single API calls and proper error handling
    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch all data in parallel but limit the amount
            const [userResponse, alertsResponse, reportsResponse, blogsResponse] = await Promise.allSettled([
                userApi.getMe(),
                alertsApi.getAlerts({ limit: 3 }), // Reduced from 5 to 3
                reportsApi.getReports({ limit: 3 }), // Reduced from 5 to 3  
                blogsApi.getPublishedBlogs({ limit: 3 }) // Reduced from 5 to 3
            ]);

            const newData: DashboardData = {
                user: null,
                alerts: [],
                reports: [],
                blogs: [],
                stats: {
                    totalAlerts: 0,
                    activeAlerts: 0,
                    totalReports: 0,
                    totalBlogs: 0
                }
            };

            // Process user data
            if (userResponse.status === 'fulfilled' && userResponse.value.success) {
                newData.user = userResponse.value.data?.user || userResponse.value.data;
            }

            // Process alerts data
            if (alertsResponse.status === 'fulfilled' && alertsResponse.value.success) {
                const alertsData = alertsResponse.value.data;
                newData.alerts = alertsData?.alerts || [];
                newData.stats.totalAlerts = alertsData?.total || newData.alerts.length;
                newData.stats.activeAlerts = newData.alerts.filter(a => a.status === 'active').length;
            }

            // Process reports data
            if (reportsResponse.status === 'fulfilled' && reportsResponse.value.success) {
                const reportsData = reportsResponse.value.data;
                newData.reports = reportsData?.reports || [];
                newData.stats.totalReports = reportsData?.total || newData.reports.length;
            }

            // Process blogs data
            if (blogsResponse.status === 'fulfilled' && blogsResponse.value.success) {
                const blogsData = blogsResponse.value.data;
                newData.blogs = Array.isArray(blogsData) ? blogsData : (blogsData?.blogs || []);
                newData.stats.totalBlogs = blogsData?.total || newData.blogs.length;
            }

            setDashboardData(newData);

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to load dashboard data';
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Only fetch data once on mount - back to simple approach
    useEffect(() => {
        fetchDashboardData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInHours < 24) return `${diffInHours}h ago`;
        if (diffInDays < 7) return `${diffInDays}d ago`;
        return formatDate(dateString);
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'critical': return '#ef4444';
            case 'high': return '#f59e0b';
            case 'medium': return '#3b82f6';
            case 'low': return '#10b981';
            default: return '#6b7280';
        }
    };

    const getThreatScoreColor = (score: number | null) => {
        if (!score) return '#6b7280';
        if (score >= 8) return '#ef4444';
        if (score >= 6) return '#f59e0b';
        if (score >= 4) return '#3b82f6';
        return '#10b981';
    };

    const getSafeScore = (score?: number | string | null): number | null => {
        if (score === null || score === undefined) return null;
        if (typeof score === 'number') return score;
        if (typeof score === 'string') {
            const parsed = parseFloat(score);
            return isNaN(parsed) ? null : parsed;
        }
        return null;
    };

    if (loading) {
        return (
            <DashboardLayout title="Dashboard Overview">
                <div className="dashboard-overview-loading">
                    <div className="dashboard-overview-loading-spinner">
                        <i className="ri-loader-4-line"></i>
                    </div>
                    <div className="dashboard-overview-loading-text">Loading dashboard...</div>
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout title="Dashboard Overview">
                <div className="dashboard-overview-error">
                    <div className="dashboard-overview-error-icon">
                        <i className="ri-error-warning-line"></i>
                    </div>
                    <div className="dashboard-overview-error-content">
                        <h3>Failed to Load Dashboard</h3>
                        <p>{error}</p>
                        <button
                            className="dashboard-overview-retry-btn"
                            onClick={() => window.location.reload()}
                        >
                            <i className="ri-refresh-line"></i>
                            Try Again
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Dashboard Overview">
            <div className="dashboard-overview">
                {/* Welcome Section */}
                <div className="dashboard-overview-welcome">
                    <div className="dashboard-overview-welcome-content">
                        <h1>Welcome back{dashboardData.user ? `, ${dashboardData.user.name}` : ''}!</h1>
                        <p>Here's your cybersecurity overview and latest intelligence updates.</p>
                    </div>
                    <div className="dashboard-overview-welcome-time">
                        <span>{new Date().toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        })}</span>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="dashboard-overview-stats">
                    <div className="dashboard-overview-stat-card">
                        <div className="dashboard-overview-stat-icon security">
                            <i className="ri-shield-line"></i>
                        </div>
                        <div className="dashboard-overview-stat-content">
                            <h3>{dashboardData.stats.totalAlerts}</h3>
                            <p>Total Alerts</p>
                            <span className="dashboard-overview-stat-change">
                                {dashboardData.stats.activeAlerts} active
                            </span>
                        </div>
                    </div>

                    <div className="dashboard-overview-stat-card">
                        <div className="dashboard-overview-stat-icon reports">
                            <i className="ri-file-text-line"></i>
                        </div>
                        <div className="dashboard-overview-stat-content">
                            <h3>{dashboardData.stats.totalReports}</h3>
                            <p>Threat Reports</p>
                            <span className="dashboard-overview-stat-change">
                                Intelligence data
                            </span>
                        </div>
                    </div>

                    <div className="dashboard-overview-stat-card">
                        <div className="dashboard-overview-stat-icon blogs">
                            <i className="ri-article-line"></i>
                        </div>
                        <div className="dashboard-overview-stat-content">
                            <h3>{dashboardData.stats.totalBlogs}</h3>
                            <p>Blog Posts</p>
                            <span className="dashboard-overview-stat-change">
                                Published content
                            </span>
                        </div>
                    </div>

                    <div className="dashboard-overview-stat-card">
                        <div className="dashboard-overview-stat-icon monitoring">
                            <i className="ri-eye-line"></i>
                        </div>
                        <div className="dashboard-overview-stat-content">
                            <h3>24/7</h3>
                            <p>Monitoring</p>
                            <span className="dashboard-overview-stat-change">
                                Active protection
                            </span>
                        </div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="dashboard-overview-grid">
                    {/* Recent Alerts */}
                    <div className="dashboard-overview-card">
                        <div className="dashboard-overview-card-header">
                            <h3>
                                <i className="ri-alarm-warning-line"></i>
                                Recent Alerts
                            </h3>
                            <a href="/dashboard/alerts" className="dashboard-overview-card-link">
                                View All <i className="ri-arrow-right-line"></i>
                            </a>
                        </div>
                        <div className="dashboard-overview-card-content">
                            {dashboardData.alerts.length === 0 ? (
                                <div className="dashboard-overview-empty">
                                    <i className="ri-shield-check-line"></i>
                                    <p>No recent alerts</p>
                                    <span>All systems secure</span>
                                </div>
                            ) : (
                                <div className="dashboard-overview-list">
                                    {dashboardData.alerts.slice(0, 5).map((alert) => (
                                        <div key={alert.id} className="dashboard-overview-list-item">
                                            <div className="dashboard-overview-item-icon">
                                                <i className="ri-alert-line"></i>
                                            </div>
                                            <div className="dashboard-overview-item-content">
                                                <h4>{alert.title}</h4>
                                                <p>{alert.description}</p>
                                                <div className="dashboard-overview-item-meta">
                                                    <span 
                                                        className="dashboard-overview-item-badge"
                                                        style={{
                                                            backgroundColor: `${getPriorityColor(alert.priority)}20`,
                                                            color: getPriorityColor(alert.priority)
                                                        }}
                                                    >
                                                        {alert.priority}
                                                    </span>
                                                    <span className="dashboard-overview-item-category">
                                                        {alert.category}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="dashboard-overview-item-time">
                                                {getTimeAgo(alert.created_at)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Reports */}
                    <div className="dashboard-overview-card">
                        <div className="dashboard-overview-card-header">
                            <h3>
                                <i className="ri-file-shield-line"></i>
                                Recent Reports
                            </h3>
                            <a href="/dashboard/reports" className="dashboard-overview-card-link">
                                View All <i className="ri-arrow-right-line"></i>
                            </a>
                        </div>
                        <div className="dashboard-overview-card-content">
                            {dashboardData.reports.length === 0 ? (
                                <div className="dashboard-overview-empty">
                                    <i className="ri-file-text-line"></i>
                                    <p>No recent reports</p>
                                    <span>Check back later</span>
                                </div>
                            ) : (
                                <div className="dashboard-overview-list">
                                    {dashboardData.reports.slice(0, 5).map((report) => (
                                        <div key={report.id} className="dashboard-overview-list-item">
                                            <div className="dashboard-overview-item-icon">
                                                <i className="ri-file-text-line"></i>
                                            </div>
                                            <div className="dashboard-overview-item-content">
                                                <h4>{report.title}</h4>
                                                <p>{report.description}</p>
                                                <div className="dashboard-overview-item-meta">
                                                    {report.threatScore && (
                                                        <span 
                                                            className="dashboard-overview-item-badge"
                                                            style={{
                                                                backgroundColor: `${getThreatScoreColor(getSafeScore(report.threatScore))}20`,
                                                                color: getThreatScoreColor(getSafeScore(report.threatScore))
                                                            }}
                                                        >
                                                            Score: {(() => {
                                                                const score = getSafeScore(report.threatScore);
                                                                return score !== null ? score.toFixed(1) : 'N/A';
                                                            })()}
                                                        </span>
                                                    )}
                                                    {report.platform && (
                                                        <span className="dashboard-overview-item-category">
                                                            {report.platform.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="dashboard-overview-item-time">
                                                {getTimeAgo(report.createdAt)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Blogs */}
                    <div className="dashboard-overview-card">
                        <div className="dashboard-overview-card-header">
                            <h3>
                                <i className="ri-article-line"></i>
                                Recent Blogs
                            </h3>
                            <a href="/dashboard/blogs" className="dashboard-overview-card-link">
                                View All <i className="ri-arrow-right-line"></i>
                            </a>
                        </div>
                        <div className="dashboard-overview-card-content">
                            {dashboardData.blogs.length === 0 ? (
                                <div className="dashboard-overview-empty">
                                    <i className="ri-article-line"></i>
                                    <p>No recent blog posts</p>
                                    <span>Stay tuned for updates</span>
                                </div>
                            ) : (
                                <div className="dashboard-overview-list">
                                    {dashboardData.blogs.slice(0, 5).map((blog) => (
                                        <div key={blog.id} className="dashboard-overview-list-item">
                                            <div className="dashboard-overview-item-icon">
                                                <i className="ri-article-line"></i>
                                            </div>
                                            <div className="dashboard-overview-item-content">
                                                <h4>{blog.title}</h4>
                                                <p>{blog.excerpt || 'No excerpt available'}</p>
                                                <div className="dashboard-overview-item-meta">
                                                    {blog.category && (
                                                        <span className="dashboard-overview-item-category">
                                                            {blog.category}
                                                        </span>
                                                    )}
                                                    <span className="dashboard-overview-item-views">
                                                        <i className="ri-eye-line"></i>
                                                        {blog.views} views
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="dashboard-overview-item-time">
                                                {getTimeAgo(blog.createdAt)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default DashboardOverview;
